import { Component, inject } from '@angular/core';
import { NotifyService, NotifyVariant } from '../../services/notify.service';

@Component({
  selector: 'app-notify-dialog',
  standalone: true,
  template: `
    <!-- ═══ Notify (แจ้งเตือน) ═══ -->
    @if (svc.notify$().visible; as _) {
      @let n = svc.notify$().options!;
      <div class="fixed inset-0 bg-black/50 backdrop-blur-sm z-10001 flex items-center justify-center p-4"
           (click)="svc.closeNotify()">
        <div class="bg-surface-raised rounded-2xl w-full max-w-sm p-8 text-center border border-border-subtle"
             style="box-shadow: var(--shadow-xl)"
             (click)="$event.stopPropagation()">
          <div class="w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center"
               [class]="iconBg(n.variant)">
            <span class="material-symbols-outlined text-3xl" [class]="iconColor(n.variant)">{{ n.icon }}</span>
          </div>
          <p class="text-lg font-semibold text-text-primary mb-1">{{ n.title }}</p>
          @if (n.detail) {
            <p class="text-sm text-text-secondary mt-1">{{ n.detail }}</p>
          }
          @if (!n.autoDismissMs) {
            <button class="mt-6 mx-auto flex items-center justify-center gap-1.5 text-sm font-semibold text-white rounded-full py-2.5 px-6 cursor-pointer transition-all"
                    style="background: linear-gradient(135deg, var(--color-accent), var(--color-accent-dark)); box-shadow: 0 2px 8px rgba(34,197,94,0.3);"
                    (click)="svc.closeNotify()">{{ n.okText }}</button>
          }
        </div>
      </div>
    }

    <!-- ═══ Confirm (ยืนยัน) ═══ -->
    @if (svc.confirm$().visible; as _) {
      @let c = svc.confirm$().options!;
      @let loading = svc.confirm$().loading;
      <div class="fixed inset-0 bg-black/50 backdrop-blur-sm z-10001 flex items-center justify-center p-4"
           (click)="svc.resolveConfirm(false)">
        <div class="bg-surface-raised rounded-2xl w-full max-w-sm p-6 space-y-4 border border-border-subtle"
             style="box-shadow: var(--shadow-xl)"
             (click)="$event.stopPropagation()">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                 [class]="confirmIconBg(c.variant!)">
              <span class="material-symbols-outlined" [class]="confirmIconColor(c.variant!)">{{ c.icon }}</span>
            </div>
            <h2 class="font-bold text-text-primary text-lg">{{ c.title }}</h2>
          </div>
          <p class="text-sm text-text-secondary">{{ c.message }}</p>
          @if (c.subMessage) {
            <p class="text-xs text-text-secondary">{{ c.subMessage }}</p>
          }
          <div class="flex gap-3 justify-end pt-2">
            <button (click)="svc.resolveConfirm(false)" [disabled]="loading"
                    class="px-5 py-2.5 rounded-full text-sm font-semibold border-2 disabled:opacity-50 transition-all cursor-pointer"
                    style="border-color: var(--color-accent); color: var(--color-accent-dark); background: transparent;"
                    onmouseover="this.style.background='var(--color-accent-lightest)'" onmouseout="this.style.background='transparent'">
              {{ c.cancelText }}
            </button>
            <button (click)="svc.resolveConfirm(true)" [disabled]="loading"
                    class="px-5 py-2.5 rounded-full text-sm font-semibold text-white disabled:opacity-50 transition-all cursor-pointer"
                    [style.background]="confirmBtnBg(c.variant!)"
                    [style.box-shadow]="confirmBtnShadow(c.variant!)">
              {{ loading ? 'กำลังดำเนินการ...' : c.confirmText }}
            </button>
          </div>
        </div>
      </div>
    }
  `,
})
export class NotifyDialog {
  protected readonly svc = inject(NotifyService);

  /** พื้นหลัง icon ของ notify */
  iconBg(variant: NotifyVariant): string {
    switch (variant) {
      case 'success': return 'bg-green-100 dark:bg-green-900/30';
      case 'error':   return 'bg-red-100 dark:bg-red-900/30';
      case 'warning': return 'bg-amber-100 dark:bg-amber-900/30';
      case 'info':    return 'bg-blue-100 dark:bg-blue-900/30';
    }
  }

  /** สี icon ของ notify */
  iconColor(variant: NotifyVariant): string {
    switch (variant) {
      case 'success': return 'text-green-600 dark:text-green-400';
      case 'error':   return 'text-red-600 dark:text-red-400';
      case 'warning': return 'text-amber-600 dark:text-amber-400';
      case 'info':    return 'text-blue-600 dark:text-blue-400';
    }
  }

  /** พื้นหลัง icon ของ confirm */
  confirmIconBg(variant: string): string {
    switch (variant) {
      case 'danger':  return 'bg-red-100 dark:bg-red-900/30';
      case 'warning': return 'bg-amber-100 dark:bg-amber-900/30';
      case 'info':    return 'bg-blue-100 dark:bg-blue-900/30';
      default:        return 'bg-amber-100 dark:bg-amber-900/30';
    }
  }

  /** สี icon ของ confirm */
  confirmIconColor(variant: string): string {
    switch (variant) {
      case 'danger':  return 'text-red-600 dark:text-red-400';
      case 'warning': return 'text-amber-600 dark:text-amber-400';
      case 'info':    return 'text-blue-600 dark:text-blue-400';
      default:        return 'text-amber-600 dark:text-amber-400';
    }
  }

  /** background ปุ่ม confirm */
  confirmBtnBg(variant: string): string {
    if (variant === 'danger') return 'linear-gradient(135deg, #ef4444, #dc2626)';
    return 'linear-gradient(135deg, var(--color-accent), var(--color-accent-dark))';
  }

  /** shadow ปุ่ม confirm */
  confirmBtnShadow(variant: string): string {
    if (variant === 'danger') return '0 2px 8px rgba(239,68,68,0.3)';
    return '0 2px 8px rgba(34,197,94,0.3)';
  }
}
